import { ScriptLoader } from '@thiememeulenhoff/js-sdk';
import { NAVIGATE_EVENT } from '@thiememeulenhoff/types';
import { Injectable } from '@angular/core';
import { PlayerError, PlayerState } from './read-speaker-state.enum';
import { config } from './read-speaker.config';
import { deepQuerySelectorAll } from '../../helpers';

declare global {
  interface Window {
    rsConf: any; // eslint-disable-line
    rspkr: any; // eslint-disable-line
  }
}

export interface ReadSpeakerEvent {
  element: HTMLElement;
  event: PlayerState | PlayerError;
}

interface ReadSpeakerSubscription {
  element: HTMLElement;
  contentElement: Element | null | undefined;
  callback: (event: ReadSpeakerEvent) => void;
}

interface ScrollSubscription {
  element: Element;
  contentElement: Element;
  callback: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class ReadSpeakerService {
  // eslint-disable-next-line no-use-before-define
  private static instance: ReadSpeakerService;

  private subscriptions: Map<string, ReadSpeakerSubscription> = new Map();

  private scrollSubscriptions: Map<string, ScrollSubscription> = new Map();

  private activePlayerId = '';

  #speedDebounceTimer: number | null = null;

  private constructor() {
    // Because this service can be created as Injectable(providedIn:root) and it can be created
    // through the static get function by the webcomponents we set the static instance also in the constructor
    // so the get function will return the Injectable-created service for the webcomponents
    ReadSpeakerService.instance = this;

    this.#initReadSpeaker();

    window.addEventListener(
      NAVIGATE_EVENT,
      this.#destroyActivePlayer as EventListener,
    );
  }

  public static get(): ReadSpeakerService {
    if (!ReadSpeakerService.instance) {
      ReadSpeakerService.instance = new ReadSpeakerService();
    }

    return ReadSpeakerService.instance;
  }

  public register(
    element: HTMLElement,
    contentElement: Element | null | undefined,
    callback: (event: ReadSpeakerEvent) => void,
  ): void {
    if (element instanceof HTMLElement) {
      this.subscriptions.set(element.id, { element, contentElement, callback });
    }
  }

  public registerScrollContainer(
    id: string,
    element: Element,
    contentElement: Element,
  ): void {
    this.unregisterScrollContainer(id);

    // Add RS_SKIP_ORG to all initially skipped elements
    const elements: Array<Element> = deepQuerySelectorAll(
      '.RS_SKIP',
      contentElement,
    );
    elements.forEach((element) => {
      element.classList.toggle('RS_SKIP_ORG', true);
    });

    const callback = () => this.#handleScrollEnd(id);

    const subscription = {
      element,
      contentElement,
      callback,
    };

    this.scrollSubscriptions.set(id, subscription);
    element.addEventListener('scrollend', callback);
  }

  public unregisterScrollContainer(id: string): void {
    const subscription = this.scrollSubscriptions.get(id);

    if (subscription) {
      subscription.element.removeEventListener(
        'scrollend',
        subscription.callback,
      );
      this.scrollSubscriptions.delete(id);
    }
  }

  public set speed(speed: number) {
    window.rspkr?.st?.set('hlspeed', speed);

    if (this.#speedDebounceTimer !== null) {
      window.clearTimeout(this.#speedDebounceTimer);
    }

    this.#speedDebounceTimer = window.setTimeout(() => {
      this.stop();
      this.play(this.activePlayerId);

      this.#speedDebounceTimer = null;
    }, 300);
  }

  public get speed(): number {
    return window.rspkr?.st?.get('hlspeed');
  }

  public play(playerId: string) {
    // If there is an active player, we stop and destroy this player.
    // This way the new speed and/or the RSPEAK_START position is redetected.
    this.#destroyActivePlayer();

    this.activePlayerId = playerId;

    const element = this.subscriptions.get(playerId)?.element;
    const playButtonElement: HTMLElement | null | undefined =
      element?.querySelector('.rsbtn_play');
    playButtonElement?.click();

    window.rspkr?.ui?.setActivePlayer(element);

    window.dispatchEvent(new CustomEvent('rs-active'));
  }

  public resume() {
    window.rspkr?.ui?.getActivePlayer()?.play();

    window.dispatchEvent(new CustomEvent('rs-active'));
  }

  public pause() {
    window.rspkr?.ui?.getActivePlayer()?.pause();

    window.dispatchEvent(new CustomEvent('rs-inactive'));
  }

  public stop() {
    window.rspkr?.ui?.getActivePlayer()?.stop();
    this.#resetActivePlayerContent();

    window.dispatchEvent(new CustomEvent('rs-inactive'));
  }

  #resetActivePlayerContent() {
    // Readspeaker does not clean up it's highlighting code correctly, so after a stop
    // this script is triggered and in cleans all rs:span nodes in the readable content
    // which makes the highlighting work every time
    const subscription = this.subscriptions.get(this.activePlayerId);

    if (subscription && subscription.contentElement instanceof Element) {
      const elements = deepQuerySelectorAll(
        '.rs_textnode',
        subscription.contentElement,
      );

      elements.forEach((element) =>
        element.replaceWith(element.textContent ?? ''),
      );
    }
  }

  #destroyActivePlayer() {
    if (window.rspkr?.ui?.getActivePlayer()) {
      window.rspkr?.ui?.getActivePlayer()?.stop();
      window.rspkr?.ui?.destroyActivePlayer();

      window.dispatchEvent(new CustomEvent('rs-inactive'));
    }
  }

  #initReadSpeaker(): void {
    const self = this; //eslint-disable-line

    window.rsConf = {
      general: {
        ...config.general,
        shadowDomSelector: [
          'tmwc-content-figure',
          'tmwc-content-player',
          'tmwc-content-theory',
          'tmwc-content-view',
          'tmwc-content-interaction',
        ],
      },
      settings: { ...config.settings },
      ui: { ...config.ui },
      cb: {
        ui: {
          play() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Playing,
            });
          },
          pause() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Paused,
            });
          },
          stop() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Stopped,
            });
          },
        },
        audio: {
          loaded() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Playing,
            });
          },
          ended() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerState.Stopped,
            });
          },
          loadError() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerError.AudioLoadError,
            });
          },
          nosound() {
            self.#dispatchLocalEvent({
              element: this as unknown as HTMLElement,
              event: PlayerError.AudioNoSound,
            });
          },
        },
      },
    };

    ScriptLoader.loadScript({
      src: 'https://cdn-eu.readspeaker.com/script/5797/webReader/webReader.js?pids=wr&noDefaultSkin=1&mobile=0',
      id: 'rs_req_Init',
      async: false,
    });
  }

  #dispatchLocalEvent(event: ReadSpeakerEvent): void {
    const subscription = this.subscriptions.get(event.element.id);
    if (subscription) {
      subscription.callback(event);
    }
  }

  #handleScrollEnd = (id: string) => {
    const subscription = this.scrollSubscriptions.get(id);

    if (subscription) {
      this.#disableAllBeforeFirstVisibleReadspeakerElement(subscription);
    }
  };

  #disableAllBeforeFirstVisibleReadspeakerElement(
    subscription: ScrollSubscription,
  ): void {
    const baseY = subscription.element.getBoundingClientRect().y;
    if (!baseY) {
      return;
    }
    const elements = deepQuerySelectorAll(
      '*:not(.RS_SKIP_ORG)',
      subscription.contentElement,
    );
    elements.forEach((element) => {
      element.classList.toggle('RS_SKIP', false);
    });

    const index = this.#getFirstVisibleElementIndex(elements, baseY);
    if (index === -1) {
      return;
    }

    elements.slice(0, index).forEach((element) => {
      element.classList.toggle('RS_SKIP', true);
    });
  }

  #getFirstVisibleElementIndex(
    elements: Array<Element>,
    baseY: number,
  ): number {
    return elements.findIndex((element) => {
      if (window.getComputedStyle(element).position === 'sticky') {
        return false;
      }

      return element.getBoundingClientRect().y - baseY >= 0;
    });
  }
}
